<template>
	<Default :loading="loading">
		<div class="sticky top-14 bg-white z-10">
			<div class="flex items-center justify-between border-b border-gray-200 px-4 sm:px-6 md:px-5 h-16">
				<Button color="tertiary" @click="back">
					<icon name="ri-arrow-left-line" class="mr-1" />
					Volver
				</Button>
			</div>
		</div>

		<div class="p-2.5 bg-white rounded-sm border border-gray-200 mb-4">
			<div class="p-4 flex flex-col gap-6">
				<div class="p-5 md:p-8 lg:p-10 text-center relative rounded-lg overflow-hidden">
					<img :src="decorationDesktop" alt="" class="hidden md:block absolute top-0 left-0 w-full h-full object-cover" />
					<img :src="decorationMobile" alt="" class="block md:hidden absolute top-0 left-0 w-full h-full object-cover" />
					<span class="z-10 relative text-white uppercase font-semibold text-[23px] leading-[125%]">Datos distribuidora</span>
				</div>
				<ContractSummaryEnergy v-if="deal && deal.contract" :contract="deal.contract" :project="project" :blue="true" />
				<div v-if="readings && readings.length > 0" class="rounded-lg overflow-hidden border border-gray-200">
					<div class="bg-primary-600 text-white p-3 flex items-center gap-2">
						<OhVueIcon name="ri-information-line" />
						<span class="uppercase text-sm font-semibold">Lecturas de consumo</span>
					</div>
					<Accordion title="Lecturas de activa" class="!rounded-none !border-t-0 !border-x-0">
						<div class="overflow-x-auto">
							<table>
								<thead>
									<tr>
										<td>Fecha</td>
										<td>Tipo</td>
										<td v-for="i in 6">Consumo P{{ i }}</td>
									</tr>
								</thead>
								<tbody>
									<tr v-for="reading in readings">
										<td>{{ dayjs(reading.FLectM1).format('DD/MM/YYYY') }}</td>
										<td>{{ reading.RE_M1 === 'R' ? 'Real' : 'Estimada' }}</td>
										<td>{{ Math.floor(reading.CActP1M1) }}</td>
										<td>{{ Math.floor(reading.CActP2M1) }}</td>
										<td>{{ Math.floor(reading.CActP3M1) }}</td>
										<td>{{ Math.floor(reading.CActP4M1) }}</td>
										<td>{{ Math.floor(reading.CActP5M1) }}</td>
										<td>{{ Math.floor(reading.CActP6M1) }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</Accordion>
					<Accordion title="Lecturas de reactiva" class="!rounded-none !border-x-0">
						<div class="overflow-x-auto">
							<table>
								<thead>
									<tr>
										<td>Fecha</td>
										<td>Tipo</td>
										<td v-for="i in 6">Consumo P{{ i }}</td>
									</tr>
								</thead>
								<tbody>
									<tr v-for="reading in readings">
										<td>{{ dayjs(reading.PublishDate).format('DD/MM/YYYY') }}</td>
										<td>{{ reading.RE_M1 === 'R' ? 'Real' : 'Estimada' }}</td>
										<td>{{ Math.floor(reading.CReacP1M1) }}</td>
										<td>{{ Math.floor(reading.CReacP2M1) }}</td>
										<td>{{ Math.floor(reading.CReacP3M1) }}</td>
										<td>{{ Math.floor(reading.CReacP4M1) }}</td>
										<td>{{ Math.floor(reading.CReacP5M1) }}</td>
										<td>{{ Math.floor(reading.CReacP6M1) }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</Accordion>
					<Accordion title="Lecturas de maxímetro" class="!rounded-lg !rounded-t-none !border-x-0">
						<div class="overflow-x-auto">
							<table>
								<thead>
									<tr>
										<td>Fecha</td>
										<td>Tipo</td>
										<td v-for="i in 6">Consumo P{{ i }}</td>
									</tr>
								</thead>
								<tbody>
									<tr v-for="reading in readings">
										<td>{{ dayjs(reading.PublishDate).format('DD/MM/YYYY') }}</td>
										<td>{{ reading.RE_M1 === 'R' ? 'Real' : 'Estimada' }}</td>
										<td>{{ Math.floor(reading.CPotP1M1) }}</td>
										<td>{{ Math.floor(reading.CPotP2M1) }}</td>
										<td>{{ Math.floor(reading.CPotP3M1) }}</td>
										<td>{{ Math.floor(reading.CPotP4M1) }}</td>
										<td>{{ Math.floor(reading.CPotP5M1) }}</td>
										<td>{{ Math.floor(reading.CPotP6M1) }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</Accordion>
				</div>
			</div>
		</div>
	</Default>
</template>

<style scoped lang="scss">
table {
	@apply font-bold w-full uppercase border-2 border-gray-200 mt-3;
	thead {
		@apply border-b-2 border-gray-200;
		td {
			@apply py-2.5 px-3 text-light-blue-500 whitespace-nowrap min-w-[90px];
		}
		td:not(:first-child) {
			@apply text-center;
		}
	}
	tbody {
		tr:nth-child(even) {
			@apply bg-gray-100;
		}
		tr:not(:last-child) {
			@apply border-b-2 border-gray-200;
		}
		td {
			@apply py-2 px-3;
			&:not(:first-child) {
				@apply text-center;
			}
		}
	}
}
</style>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Button from '../../components/Button.vue'
import ContractSummaryEnergy from '../../components/leads/ContractSummaryEnergy.vue'
import { get } from '../../services/api'
import Default from '../Default.vue'
import { Reading } from '../../interfaces/Reading'
import decorationDesktop from '@/apps/go/images/leads/bg-price-desktop.png'
import decorationMobile from '@/apps/go/images/leads/bg-price-mobile.png'
import { Deal } from '../../interfaces/Deal'
import Accordion from '../../components/Accordion.vue'
import { OhVueIcon } from 'oh-vue-icons'
import { dayjs } from '@/services/dayjs'

const route = useRoute()
const loading = ref(false)
const deal = ref<Deal | null>(null)
const readings = ref<Reading[]>([])
const error = ref({})

const project = route.query.project?.toString() || ''
const id = route.query._id?.toString() || ''

const fetch = async () => {
	if (!project || !id) return

	loading.value = true
	error.value = <any>{}
	try {
		const { data } = await get({ name: 'deal.preview', params: { project, _id: id } })
		const { deal: dealData, readings: readingsData } = data
		if (dealData) {
			deal.value = dealData
		}
		if (readingsData) {
			readings.value = readingsData
		}
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const back = () => {
	history.back()
}

onMounted(async () => {
	fetch()
})
</script>
